import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';

type FormCardProps = {
    icon: any;
    name: string;
    children: React.ReactNode;
    onboarding?: boolean;
    description?: string;
};

export const FormCard: React.FC<FormCardProps> = ({
    icon,
    name,
    children,
    onboarding = false,
    description,
}): JSX.Element => {
    if (!onboarding) {
        return (
            <Box sx={{ marginTop: 3, '&:first-of-type': { marginTop: 0 } }}>
                <Typography
                    sx={(theme) => ({
                        width: '100%',
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '15px',
                        fontFamily: 'YekanBalkhHeavy',
                        paddingBottom: theme.spacing(1),
                        marginBottom: theme.spacing(2.5),
                        borderBottom: '1px solid',
                        borderColor: 'divider',

                        i: {
                            marginInlineEnd: theme.spacing(1),
                            color: 'text.secondary',
                        },
                    })}>
                    {icon}
                    {name}
                </Typography>
                {children}
            </Box>
        );
    }

    return (
        <Box
            sx={(theme) => ({
                position: 'relative',
                mt: 1.25,
                p: { xs: 1.4, sm: 1.75, lg: 1.9 },
                borderRadius: 1.5,
                border: '1px solid',
                borderColor:
                    theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.075)
                        : alpha(theme.palette.common.black, 0.07),
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.34)
                        : alpha(theme.palette.common.white, 0.7),
                transition: theme.transitions.create([
                    'border-color',
                    'box-shadow',
                    'background-color',
                ]),

                '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.16),
                    boxShadow: `0 10px 30px ${alpha(
                        theme.palette.mode === 'dark'
                            ? theme.palette.common.black
                            : '#172033',
                        theme.palette.mode === 'dark' ? 0.08 : 0.035,
                    )}`,
                },
            })}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                }}>
                <Box
                    sx={(theme) => ({
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color:
                            theme.palette.mode === 'dark'
                                ? 'primary.light'
                                : 'primary.main',
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === 'dark' ? 0.15 : 0.075,
                        ),
                        i: { fontSize: 14 },
                    })}>
                    {icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'YekanBalkhHeavy',
                            fontSize: 14,
                            lineHeight: 1.6,
                        }}>
                        {name}
                    </Typography>
                    {description && (
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: 10.5,
                                lineHeight: 1.65,
                                mt: 0.15,
                            }}>
                            {description}
                        </Typography>
                    )}
                </Box>
            </Box>

            {children}
        </Box>
    );
};
